import { useEffect, type RefObject } from "react";
import type { DamageType, EdgeMap, SquareCell, TerrainCell, TerrainMap, TileTypeId } from "@lancer-vtt/domain";
import type { CombatBrush, CoverGrade, Vertex, WallHeight } from "../combatAuthoring";
import type { CombatBoardHandle } from "../pixiCombatBoard";
import { authoringPreviewSpecs } from "./authoringPreviewSpecs";

/**
 * Authoring-preview ghosts (§3 row 14): resolve whichever gesture is currently open into a
 * renderer-agnostic spec list ({@link authoringPreviewSpecs} — pure) and hand it to
 * `pixiCombatBoard.ts`, which only draws. Kept apart from `useBoardSync` because this layer is
 * driven by LOCAL, uncommitted gesture state rather than encounter state.
 */
export function useAuthoringPreviewSync({
  boardReady,
  boardHandleRef,
  tileBoxPreview,
  tilePreview,
  hoverCell,
  hoverTarget,
  wallPreview,
  roomPreview,
  brush,
  terrain,
  tile,
  coverGrade,
  hazard,
  wallHeight,
  wallCoverGrade,
  cols,
  rows,
  edges,
}: {
  boardReady: boolean;
  boardHandleRef: RefObject<CombatBoardHandle | null>;
  tileBoxPreview: { start: SquareCell; end: SquareCell; erase: boolean } | null;
  tilePreview: { cells: SquareCell[]; target: TerrainCell } | null;
  hoverCell: SquareCell | null;
  hoverTarget: TerrainCell | null;
  wallPreview: { from: Vertex; to: Vertex; erase: boolean } | null;
  roomPreview: { start: SquareCell; end: SquareCell; erase: boolean } | null;
  brush: CombatBrush | null;
  terrain: TerrainMap;
  tile: TileTypeId;
  coverGrade: CoverGrade;
  hazard: DamageType;
  wallHeight: WallHeight;
  wallCoverGrade: CoverGrade;
  cols: number;
  rows: number;
  edges: EdgeMap;
}) {
  useEffect(() => {
    if (!boardReady) return;
    boardHandleRef.current?.setAuthoringPreview(
      authoringPreviewSpecs({
        tileBoxPreview,
        tilePreview,
        hoverCell,
        hoverTarget,
        wallPreview,
        roomPreview,
        brush,
        terrain,
        tile,
        coverGrade,
        hazard,
        wallHeight,
        wallCoverGrade,
        cols,
        rows,
        edges,
      }),
    );
  }, [
    boardReady,
    boardHandleRef,
    tileBoxPreview,
    tilePreview,
    hoverCell,
    hoverTarget,
    wallPreview,
    roomPreview,
    brush,
    terrain,
    tile,
    coverGrade,
    hazard,
    wallHeight,
    wallCoverGrade,
    cols,
    rows,
    edges,
  ]);
}
